import { type ICdpResponse } from '@sitecore/engage';
import { logWarning, logInfo } from 'lib/util/errors';

/**
 * @function processCdpResponse
 * @description Processes and logs CDP (Customer Data Platform) responses from Engage SDK operations.
 * Logs successful responses or errors for debugging and monitoring purposes.
 * @param {string} source - A descriptive label identifying the source/context of the CDP call
 * @param {Promise<ICdpResponse | null>} promise - Promise resolving to a CDP response or null
 * @returns {Promise<ICdpResponse | null>} The original promise passed in (for chaining)
 * @example
 * processCdpResponse('pageView', engage.pageView({ channel: 'WEB' }));
 */
export const processCdpResponse = (source: string, promise: Promise<ICdpResponse | null>) => {
  promise
    .then((response) => {
      if (response) {
        try {
          if (response.status !== 'OK') {
            logWarning(`[${source}]: CDP Response Error Status: ${JSON.stringify(response)}`);
          } else {
            logInfo(`[${source}]: CDP Response Successful`);
          }
          return response;
        } catch (error) {
          // Handle circular references or other JSON.stringify errors
          logWarning(`[${source}]: Unable to stringify CDP response: ${error}`);
        }
      } else {
        logInfo(`[${source}]: No CDP response returned`);
      }
      return response;
    })
    .catch((error) => {
      logWarning(`[${source}]: CDP Response Error: ${error}`);
    });
  return promise;
};
